import { Component, inject, Injectable } from '@angular/core';
import { CommonModule } from '@angular/common';
import confetti from 'canvas-confetti';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { BehaviorSubject } from 'rxjs';
import { LocalStorageService } from '../../core/service/local-storage/local-storage.service';

export interface ConfettiConfig {
  particleCount?: number;
  spread?: number;
  startVelocity?: number;
  decay?: number;
  gravity?: number;
  drift?: number;
  ticks?: number;
  origin?: { x: number; y: number };
  colors?: string[];
  shapes?: ('square' | 'circle')[];
  scalar?: number;
  zIndex?: number;
  disableForReducedMotion?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ConfettiService {
    localStorageService = inject(LocalStorageService);
  private lottieSource = new BehaviorSubject<AnimationOptions | null>(null);
  lottie$ = this.lottieSource.asObservable();

  /**
   * Reproduce una animación Lottie
   */
  playLottie(options: AnimationOptions): void {
    this.lottieSource.next(options);
  }

  /**
   * Detiene la animación Lottie
   */
  stopLottie(): void {
    this.lottieSource.next(null);
  }

  /**
   * Lanza fuegos artificiales Lottie
   * Requiere el archivo src/assets/lottie/Confetti.json
   */
  fireworksLottie(): void {
    this.playLottie({
      path: '/assets/lottie/Confetti.json', // Ruta exacta del archivo descargado
      loop: true,
      autoplay: true
    });

    // Detener automáticamente después de 15 segundos
    setTimeout(() => {
      this.stopLottie();
    }, 4000);
  }

  /**
   * Lanza confetti básico
   */
  basic(config?: Partial<ConfettiConfig>): void {
    const defaultConfig: ConfettiConfig = {
      particleCount: 100,
      spread: 70,
      origin: {
        y: 0.6,
        x: 0
      }
    };

    confetti({ ...defaultConfig, ...config });
  }

  /**
   * Lanza confetti desde los lados
   */
  fromSides(config?: Partial<ConfettiConfig>): void {
    const count = config?.particleCount || 200;
    const defaults = {
      origin: { y: 0.7 },
      ...config
    };

    // Lado izquierdo
    confetti({
      ...defaults,
      particleCount: count / 2,
      angle: 60,
      spread: 55,
      origin: { x: 0, y: 0.7 }
    });

    // Lado derecho
    confetti({
      ...defaults,
      particleCount: count / 2,
      angle: 120,
      spread: 55,
      origin: { x: 1, y: 0.7 }
    });
  }

  /**
   * Lanza emojis de corazones desde los lados
   */
  heartsFromSides(config?: Partial<ConfettiConfig>): void {
    const count = config?.particleCount || 150;
    const defaults = {
      origin: { y: 0.7 },
      shapes: [confetti.shapeFromText({ text: '❤️', scalar: 2 }), confetti.shapeFromText({ text: '💖', scalar: 2 }), confetti.shapeFromText({ text: '💕', scalar: 2 }), confetti.shapeFromText({ text: '💗', scalar: 2 })],
      scalar: 1.5,
      gravity: 0.8,
      drift: 0.1,
      ...config
    };

    // Lado izquierdo
    confetti({
      ...defaults,
      particleCount: count / 2,
      angle: 60,
      spread: 55,
      origin: { x: 0, y: 0.7 }
    });

    // Lado derecho
    confetti({
      ...defaults,
      particleCount: count / 2,
      angle: 120,
      spread: 55,
      origin: { x: 1, y: 0.7 }
    });
  }

  /**
   * Lanza confetti en forma de lluvia
   */
  rain(duration: number = 3000, config?: Partial<ConfettiConfig>): void {
    const animationEnd = Date.now() + duration;
    const defaults = {
      startVelocity: 30,
      spread: 360,
      ticks: 60,
      zIndex: 0,
      ...config
    };

    const randomInRange = (min: number, max: number) => {
      return Math.random() * (max - min) + min;
    };

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        clearInterval(interval);
        return;
      }

      const particleCount = 50 * (timeLeft / duration);

      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });

      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
    }, 250);
  }

  /**
   * Lanza confetti con colores personalizados
   */
  withColors(colors: string[], config?: Partial<ConfettiConfig>): void {
    const defaultConfig: ConfettiConfig = {
      particleCount: 100,
      spread: 70,
      origin: {
        y: 0.6,
        x: 0
      },
      colors
    };

    confetti({ ...defaultConfig, ...config });
  }
  /**
   * Nieve cayendo de forma continua durante un tiempo
   */
  snow(duration: number = 10000, config?: Partial<ConfettiConfig>): void {
    const animationEnd = Date.now() + duration;
    const defaults = {
      particleCount: 10,
      startVelocity: 0,
      spread: 50,
      ticks: 9000,
      gravity: 0.55,
      drift: 0.02,
      colors: ['#FFFFFF'],
      shapes: ['circle'],
      scalar: 0.9,
      zIndex: 100,
      disableForReducedMotion: true,
      ...config
    } as ConfettiConfig;

    const randomX = () => Math.random();
    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) {
        clearInterval(interval);
        return;
      }

      confetti({
        ...defaults,
        origin: { x: randomX(), y: 0 }
      });
      confetti({
        ...defaults,
        origin: { x: randomX(), y: 0 }
      });
    }, 200);
  }



  /**
   * Lanza confetti de éxito (verde)
   */
  success(config?: Partial<ConfettiConfig>): void {
    this.withColors(['#10B981', '#34D399', '#6EE7B7', '#A7F3D0'], config);
  }

  /**
   * Lanza confetti de celebración (multicolor)
   */
  celebration(config?: Partial<ConfettiConfig>): void {
    this.withColors([
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4',
      '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F'
    ], config);
  }

  /**
   * Lanza confetti en forma de estrella
   */
  stars(config?: Partial<ConfettiConfig>): void {
    const defaults = {
      particleCount: 50,
      spread: 360,
      startVelocity: 30,
      gravity: 0.5,
      drift: 0,
      ticks: 200,
      origin: { x: 0.5, y: 0.5 },
      shapes: ['star' as any],
      scalar: 1.2,
      ...config
    };

    confetti(defaults);
  }

  /**
   * Detiene todas las animaciones de confetti
   */
  stop(): void {
    confetti.reset();
    this.stopLottie();
  }
}

@Component({
  selector: 'shared-confetti',
  standalone: true,
  imports: [CommonModule, LottieComponent],
  template: `
    <div *ngIf="lottieOptions$ | async as options" class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
      <div class="w-full h-full flex items-center justify-center cursor-pointer"
      (click)="confettiService.stopLottie()" >
         <ng-lottie [options]="options"></ng-lottie>
      </div>


    </div>
  `
})
export class ConfettiComponent {
  lottieOptions$;

  constructor(public confettiService: ConfettiService) {
    this.lottieOptions$ = this.confettiService.lottie$;
  }
}
